/**
 * Customer Feedback API
 *
 * Workshop-side reads for the Customer Reviews dashboard, plus the question-label
 * override endpoints.
 *
 * SCOPING: no call sends an organisation id. The backend resolves it from the
 * `organisationUuid` JWT claim and scopes every query to it, so one workshop can never
 * read another's ratings.
 */

import axiosInstance from './axiosInstance';

// ============================================
// Summary
// ============================================

/**
 * Per-question averages and the headline score for a date window.
 *
 * `from` / `to` are INCLUSIVE ISO dates (YYYY-MM-DD) matched against when the customer
 * RESPONDED — not when the vehicle was collected. A job collected on 30 June whose
 * customer replies on 3 July is a July review.
 *
 * Each bound is omitted from the query string entirely when absent rather than sent as
 * an empty string: Micronaut binds `?from=` to a LocalDate parse failure, not to null,
 * so an empty value 400s the whole request and the dashboard goes blank.
 *
 * Omitting both gives the server's default window (last 90 days).
 *
 * @param {{ from?: string, to?: string }} params
 * @returns {Promise<{
 *   questions: Array<{ slotNumber, label, enabled, defaultLabel, labelChangedAt,
 *                      averageRating, responseCount }>,
 *   overallScore: number|null,
 *   responseCount: number
 * }>}
 *   `averageRating` and `overallScore` are NULL when nobody has answered — that is
 *   distinct from a score of zero, which is not even on the 1-10 scale.
 */
export const getFeedbackSummaryApi = async ({ from, to } = {}) => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;

    const response = await axiosInstance.get('/customer-feedback/summary', { params });
    return response.data;
};

// ============================================
// Questions
// ============================================

/** All ten slots with this workshop's current labels, including disabled ones. */
export const getFeedbackQuestionsApi = async () => {
    const response = await axiosInstance.get('/customer-feedback/questions');
    return response.data;
};

/**
 * Change labels and/or enabled state for one or more slots.
 *
 * Partial payloads are legal — only the slots present are touched. A null `label` keeps
 * the current wording, so a slot can be disabled without restating its text.
 *
 * PUT with a JSON body, never a body-less POST carrying query params — that combination
 * 415s every axios call and logs nothing server-side.
 *
 * @param {Array<{ slotNumber: number, label?: string|null, enabled?: boolean|null }>} questions
 */
export const updateFeedbackQuestionsApi = async (questions) => {
    const response = await axiosInstance.put('/customer-feedback/questions', { questions });
    return response.data;
};

const customerFeedbackApi = {
    getFeedbackSummaryApi,
    getFeedbackQuestionsApi,
    updateFeedbackQuestionsApi,
};

export default customerFeedbackApi;
